import { z } from "zod";

const flightRowSchema = z.object({
  flight_row_number: z.number().min(1),
  language_id: z
    .string()
    .uuid({ message: "Language ID must be a valid UUID" })
    .nullable()
    .optional(),
  objective_id: z
    .string()
    .uuid({ message: "Objective ID must be a valid UUID" })
    .nullable()
    .optional(),
  region: z
    .string()
    .max(120, { message: "Region must be at most 120 characters" })
    .nullable()
    .optional(),
  ad_category: z
    .array(
      z.string().uuid({ message: "Each ad category must be a valid UUID" })
    )
    .min(1, { message: "At least one ad category must be provided" })
    .nullable()
    .optional(),
});

const flightSchema = z
  .object({
    campaign_id: z.string().uuid(),
    flight_number: z.number().min(1),
    start_date: z
      .string({ error: "Please enter valid Start Date" })
      .refine((val) => !isNaN(Date.parse(val)), {
        message: "Invalid start date",
      }),
    end_date: z
      .string({ error: "Please enter valid End Date " })
      .refine((val) => !isNaN(Date.parse(val)), {
        message: "Invalid end date",
      }),
    rows: z
      .array(flightRowSchema)
      .min(1, { message: "Each flight must have at least one row" }),
  })
  .superRefine((data, ctx) => {
    if (data.start_date && data.end_date) {
      if (new Date(data.end_date) < new Date(data.start_date)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "End date must be after start date",
          path: ["end_date"],
        });
      }
    }
  });

export const flightsSchema = z.array(flightSchema);

const updateFlightRowSchema = z.object({
  id: z.string().uuid({ message: "Row ID must be a valid UUID" }).optional(),

  region: z
    .string()
    .max(120, { message: "Region must be at most 120 characters" })
    .nullable()
    .optional(),

  ad_category: z
    .array(
      z.string().uuid({ message: "Each ad category must be a valid UUID" }),
      { error: "Ad categories are required" }
    )
    .min(1, { message: "At least one ad category must be provided" })
    .nullable(),
});

const singleFlightUpdateSchema = z
  .object({
    flight_number: z
      .number({ error: "Flight number is required" })
      .min(1, { message: "Flight number must be at least 1" }),

    start_date: z
      .string({ error: "Start date is required" })
      .refine((val) => !isNaN(Date.parse(val)), {
        message: "Invalid start date",
      }),

    end_date: z
      .string({ error: "End date is required" })
      .refine((val) => !isNaN(Date.parse(val)), {
        message: "Invalid end date",
      }),

    rows: z
      .array(updateFlightRowSchema, {
        error: "Flight rows are required",
      })
      .min(1, { message: "Each flight must have at least one row" }),
  })
  .superRefine((data, ctx) => {
    const start = new Date(data.start_date);
    const end = new Date(data.end_date);
    if (end < start) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "End date must be after start date",
        path: ["end_date"],
      });
    }
  });

export const UpdateFlightSchema = z.object({
  flights: z
    .array(singleFlightUpdateSchema, {
      error: "Flights are required",
    })
    .min(1, { message: "At least one flight must be provided" }),
});
